import React, { useState, useEffect } from 'react';
import { Table, Tag, Button, Select } from 'antd';
import { getRequest } from '../../../../service';
import { Edit } from "lucide-react";
import EditShiftModal from '../../../../modals/requestEditShift';
import ModalWrapper from '../../../../Modal';
import { getEmployeeId } from "../../../../utils/localStorage";
import { nowInUserTz, parseToUserTz } from "../../../../utils/dateTime";

const Attendance = () => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editShiftModalOpen, setEditShiftModalOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [shiftData, setShiftData] = useState(null);
    const [requestShifts, setRequestShifts] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('current');

    // Get employeeId from localStorage
    const employeeId = getEmployeeId();

    const getCurrentDate = () => nowInUserTz();

    const parseDate = (dateString) => parseToUserTz(dateString);

    // Helper function to format date
    const formatDate = (date, format = 'YYYY-MM-DD') => {
        if (!date) return '';
        return parseDate(date).format(format);
    };

    // Get month dates based on selection
    const getMonthDates = (monthType) => {
        const currentMonth = getCurrentDate();
        if (monthType === 'previous') {
            const previousMonth = currentMonth.subtract(1, 'month');
            return {
                startDate: previousMonth.startOf('month').format('YYYY-MM-DD'),
                endDate: previousMonth.endOf('month').format('YYYY-MM-DD')
            };
        } else {
            return {
                startDate: currentMonth.startOf('month').format('YYYY-MM-DD'),
                endDate: currentMonth.endOf('month').format('YYYY-MM-DD')
            };
        }
    };

    const { startDate, endDate } = getMonthDates(selectedMonth);

    // Fetch shift data
    const fetchShiftData = async () => {
        if (!employeeId) return;
        try {
            const response = await getRequest(`/api/companies/shifts/byEmployeeId?employeeId=${employeeId}`);
            setShiftData(response?.shift);
        } catch (error) {
            console.error('Error fetching shift data:', error);
        }
    };

    // Fetch request shifts data
    const fetchRequestShifts = async () => {
        if (!employeeId) return;
        try {
            const response = await getRequest(`/api/requestshift?employeeId=${employeeId}`);
            setRequestShifts(response?.requestShifts || []);
        } catch (error) {
            console.error('Error fetching request shifts:', error);
            setRequestShifts([]);
        }
    };

    // Check if a request already exists for a specific date
    const hasRequestForDate = (date) => {
        if (!requestShifts || requestShifts.length === 0) return false;
        
        const dateStr = parseDate(date).format('YYYY-MM-DD');
        return requestShifts.some(request => 
            parseDate(request.requestEditShiftDate).format('YYYY-MM-DD') === dateStr
        );
    };

    // Generate all days for the month including off days
    const generateAllDays = (attendanceData, shiftData) => {
        if (!shiftData?.workingDays) return attendanceData;

        const allDays = [];
        const workingDays = shiftData.workingDays.map(day => day.toLowerCase());
        
        // Get current date to limit the range
        const currentDate = getCurrentDate();
        
        // Generate all days in the month up to current date (only for current month)
        let startDateObj = parseDate(startDate);
        const endDateObj = parseDate(endDate);
        const limitDate = selectedMonth === 'current' && currentDate.isBefore(endDateObj) ? currentDate : endDateObj;
        
        while (startDateObj.isBefore(limitDate) || startDateObj.isSame(limitDate, 'day')) {
            const dayName = startDateObj.format('dddd');
            const dateStr = startDateObj.format('YYYY-MM-DD');
            
            // Check if this is a working day
            const isWorkingDay = workingDays.includes(dayName.toLowerCase());
            
            // Find existing attendance data for this date
            const existingAttendance = attendanceData.find(item => 
                parseDate(item.date).format('YYYY-MM-DD') === dateStr
            );
            
            if (existingAttendance) {
                // Add existing attendance data (regardless of check-in/check-out status)
                allDays.push({
                    ...existingAttendance,
                    isOffDay: false,
                    hasRequest: hasRequestForDate(existingAttendance.date)
                });
            } else if (!isWorkingDay) {
                // Add off day entry
                allDays.push({
                    id: `off-${dateStr}`,
                    date: dateStr,
                    checkIn: '-',
                    checkOut: '-',
                    status: 'Off Day',
                    workingHour: '-',
                    overtime: '-',
                    isOffDay: true,
                    hasRequest: false
                });
            } else {
                // Add absent entry for working days with no attendance data
                allDays.push({
                    id: `absent-${dateStr}`,
                    date: dateStr,
                    checkIn: '-',
                    checkOut: '-',
                    status: 'Absent',
                    workingHour: '-',
                    overtime: '-',
                    isOffDay: false,
                    hasRequest: hasRequestForDate(dateStr)
                });
            }
            
            startDateObj = startDateObj.add(1, 'day');
        }
        
        return allDays;
    };

    // Handle month selection change
    const handleMonthChange = (value) => {
        setSelectedMonth(value);
    };

    useEffect(() => {
        if (!employeeId) return;
        
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch shift data first
                await fetchShiftData();
                
                // Fetch request shifts data
                await fetchRequestShifts();
                
                // Then fetch attendance data
                const res = await getRequest(
                    `/api/myattendance?employeeId=${employeeId}&startDate=${startDate}&endDate=${endDate}`
                );
                
                let attendanceArray = [];
                if (res?.attendanceLogs && Array.isArray(res.attendanceLogs)) {
                    attendanceArray = res.attendanceLogs.map((item, idx) => ({
                        ...item,
                        id: item._id || item.id || idx,
                    }));
                } else if (Array.isArray(res)) {
                    attendanceArray = res.map((item, idx) => ({
                        ...item,
                        id: item._id || item.id || idx,
                    }));
                }
                
                setAttendanceData(attendanceArray);
            } catch (error) {
                console.error('Error fetching data:', error);
                setAttendanceData([]);
            } finally {
                setLoading(false);
            }
        };
        
        fetchData();
    }, [employeeId, startDate, endDate]);

    const getStatusColor = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'present':
                return 'success';
            case 'late':
                return 'warning';
            case 'absent':
                return 'error';
            case 'leave':
                return 'processing';
            case 'off day':
                return 'default';
            case 'missing':
                return 'warning';
            default:
                return 'default';
        }
    };

    const handleEditClick = (record) => {
        if (record.isOffDay || record.hasRequest) {
            return; // Don't allow editing off days or dates with existing requests
        }
        // Only allow editing for Absent or Missing status
        if (record.status?.toLowerCase() !== 'absent' && record.status?.toLowerCase() !== 'missing') {
            return;
        }
        setSelectedEntry(record);
        setEditShiftModalOpen(true);
    };

    const handleCloseEditShiftModal = () => {
        setEditShiftModalOpen(false);
        setSelectedEntry(null);
    };

    const handleEditSuccess = () => {
        // Refetch request shifts data to update the UI
        fetchRequestShifts();
        setEditShiftModalOpen(false);
        setSelectedEntry(null);
    };

    // Helper to format time in 12-hour format with AM/PM
    const formatTime12Hour = (timeStr) => {
        if (!timeStr || timeStr === "" || timeStr === "-" || timeStr === "ss") return "-";
        const [hours, minutes] = timeStr.split(":");
        let hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12;
        return `${hour.toString().padStart(2, "0")}:${minutes} ${ampm}`;
    };

    // Generate complete data including off days
    const completeAttendanceData = generateAllDays(attendanceData, shiftData);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: (date) => date ? parseDate(date).format('YYYY-MM-DD') : '',
        },
        {
            title: 'Day',
            key: 'day',
            render: (_, record) =>
                record.date ? parseDate(record.date).format('dddd') : '',
        },
        {
            title: 'Check In',
            dataIndex: 'checkIn',
            key: 'checkIn',
            render: (checkIn, record) => {
                return formatTime12Hour(checkIn);
            },
        },
        {
            title: 'Check Out',
            dataIndex: 'checkOut',
            key: 'checkOut',
            render: (checkOut, record) => {
                return formatTime12Hour(checkOut);
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status}
                </Tag>
            ),
        },
        {
            title: 'Working Hours',
            dataIndex: 'workingHour',
            key: 'workingHour',
        },
        {
            title: 'Overtime',
            dataIndex: 'overtime',
            key: 'overtime',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => {
                const isAbsentOrMissing = record.status?.toLowerCase() === 'absent' || record.status?.toLowerCase() === 'missing';
                const shouldDisable = record.isOffDay || record.hasRequest || !isAbsentOrMissing;
                
                return (
                    <Button
                        type="text"
                        icon={<Edit className="h-4 w-4" />}
                        disabled={shouldDisable}
                        onClick={() => handleEditClick(record)}
                        className={shouldDisable ? "opacity-50 cursor-not-allowed" : ""}
                        title={
                            record.hasRequest 
                                ? "Request already exists for this date" 
                                : record.isOffDay 
                                    ? "Cannot edit off days" 
                                    : !isAbsentOrMissing
                                        ? "Can only edit Absent or Missing records"
                                        : "Edit shift"
                        }
                    />
                );
            },
        },
    ];

    return (
        <>
            <div className="p-4">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">Attendance Details</h1>
                    <div>
                        <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            style={{ width: 150 }}
                            options={[
                                { value: 'current', label: 'Current Month' },
                                { value: 'previous', label: 'Previous Month' }
                            ]}
                        />
                    </div>
                </div>
                <Table
                    columns={columns}
                    dataSource={completeAttendanceData}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
            </div>
            <ModalWrapper
                open={editShiftModalOpen}
                onRequestClose={handleCloseEditShiftModal}
                title="Edit Shift"
            >
                <EditShiftModal
                    onClose={handleCloseEditShiftModal}
                    onSuccess={handleEditSuccess}
                    initialData={selectedEntry || {}}
                />
            </ModalWrapper>
        </>
    );
};

export default Attendance;